import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getReport, getMonthlyReport as getSalesAmountReport, getSalesmanReport } from "../../redux/apiCalls";
import { publicRequest } from "../../requestMethods";
import moment from "moment";
import "./reporting.css";
import { FiPlus } from "react-icons/fi";
import styled, { keyframes } from "styled-components";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import {
    MdPayment,
    MdLoyalty,
    MdLocalAtm,
    MdCardTravel,
    MdLightbulbOutline,
    MdAssignmentTurnedIn,
    MdQueryStats,
    MdAccessTime,
    MdTimerOff,
} from "react-icons/md";
import { mobile, tablet } from "../../responsive";

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
`;

const DashboardBg = styled.div`
    min-height: 100vh;
    background: #0b1220;
    color: #fff;
    padding-bottom: 3rem;
    position: relative;
    overflow: hidden;
    z-index: 1;
    width: 100%;
`;

const VideoBackground = styled.video`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -2;
    opacity: 1;
    pointer-events: none;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;

    @media (prefers-reduced-motion: reduce) {
        display: none;
    }
`;

const BackgroundOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.45);
    z-index: -1;
    pointer-events: none;
`;

const HudOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;
`;

const DashboardContainer = styled.div`
    margin: 0 auto;
    padding: 2.5rem 2rem 4rem;
    display: flex;
    flex-direction: column;
    animation: ${fadeIn} 1s ease;
    max-width: 1440px;
    width: min(100%, 1360px);
    box-sizing: border-box;

    ${tablet({
        padding: "2.25rem 1.75rem 3.5rem",
        width: "100%",
    })}

    ${mobile({
        padding: "2rem 1rem 3rem",
    })}
`;

const PageHeader = styled.div`
    margin-bottom: 2.5rem;
    text-align: center;

    ${tablet({
        marginBottom: "2.1rem",
    })}

    ${mobile({
        marginBottom: "1.8rem",
    })}
`;

const PageTitle = styled.h1`
    font-size: 2.75rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.5rem;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
    letter-spacing: 0.035em;
    background: linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    ${tablet({
        fontSize: "2.4rem",
    })}

    ${mobile({
        fontSize: "2.2rem",
    })}
`;

const PageSubtitle = styled.p`
    font-size: 1.15rem;
    color: rgba(209, 213, 219, 0.95);
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 500;
    letter-spacing: 0.02em;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);

    ${tablet({
        fontSize: "1.05rem",
    })}

    ${mobile({
        fontSize: "1rem",
    })}
`;

const ChartSection = styled.section`
    background: rgba(17, 24, 39, 0.94);
    border-radius: 20px;
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.45);
    padding: 1.85rem 1.75rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 1.75rem;

    ${tablet({
        padding: "1.6rem 1.4rem",
        marginBottom: "1.5rem",
    })}

    ${mobile({
        padding: "1.4rem 1.05rem",
        marginBottom: "1.25rem",
    })}
`;

const ChartTitle = styled.h3`
    color: #ffffff;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 700;
    font-size: 1.35rem;
    margin-bottom: 1.25rem;
    letter-spacing: 0.025em;
    position: relative;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);

    &::after {
        content: "";
        position: absolute;
        bottom: -0.6rem;
        left: 0;
        width: 3.25rem;
        height: 3px;
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.9) 0%,
            rgba(255, 255, 255, 0.3) 100%
        );
        border-radius: 2px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.28);
    }
`;

const BlueChartTitle = styled.h3`
    color: #00eaff;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 600;
    font-size: 1.2rem;
    margin-bottom: 1rem;
    transition: color 1.2s ease;
    letter-spacing: 0.025em;

    &.highlight {
        color: #f7b520;
    }
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.75rem;

    ${tablet({
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "1.35rem",
    })}

    ${mobile({
        gridTemplateColumns: "1fr",
        gap: "1.1rem",
    })}
`;

const StatCard = styled.div`
    background: linear-gradient(
        135deg,
        rgba(37, 99, 235, 0.16) 0%,
        rgba(15, 23, 42, 0.85) 100%
    );
    border-radius: 18px;
    box-shadow: 0 10px 36px rgba(15, 23, 42, 0.5);
    padding: 1.6rem 1.35rem;
    border: 1.5px solid rgba(59, 130, 246, 0.25);
    display: flex;
    flex-direction: column;
    position: relative;
    transition: box-shadow 0.35s, border 0.35s, transform 0.35s;
    overflow: hidden;
    min-height: 180px;

    &:hover {
        box-shadow: 0 15px 45px rgba(59, 130, 246, 0.35);
        border-color: rgba(59, 130, 246, 0.75);
        transform: translateY(-4px);
    }

    &::before {
        content: "";
        position: absolute;
        inset: -2px;
        opacity: 0;
        background: linear-gradient(135deg, #00eaff44, transparent);
        border-radius: 18px;
        transition: opacity 0.35s ease;
    }

    &:hover::before {
        opacity: 1;
    }

    ${tablet({
        padding: "1.45rem 1.2rem",
        minHeight: "170px",
    })}

    ${mobile({
        padding: "1.25rem 1.05rem",
        minHeight: "160px",
    })}
`;

const StatTitle = styled.div`
    font-size: 0.85rem;
    color: #cbd5f5;
    margin-bottom: 0.45rem;
    letter-spacing: 0.08em;
    font-weight: 600;
    text-transform: uppercase;
`;

const StatValue = styled.div`
    font-size: 2rem;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.35rem;
    text-shadow: 0 0 12px rgba(79, 70, 229, 0.45);

    ${mobile({
        fontSize: "1.7rem",
    })}
`;

const StatDescription = styled.div`
    font-size: 0.85rem;
    color: rgba(226, 232, 240, 0.75);
`;

const StatIcon = styled.div`
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 1.4rem;
    color: #38bdf8;
    opacity: 0.85;
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;

    ${tablet({
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "1.1rem",
    })}

    ${mobile({
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    })}
`;

const FilterCard = styled.div`
    background: rgba(15, 23, 42, 0.85);
    border-radius: 14px;
    border: 1.5px solid rgba(59, 130, 246, 0.2);
    padding: 1.2rem 1.1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    ${tablet({
        padding: "1.15rem 1rem",
    })}

    ${mobile({
        padding: "1.05rem 0.95rem",
    })}
`;

const FilterLabel = styled.label`
    font-size: 0.8rem;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const FilterInput = styled.input`
    width: 100%;
    border-radius: 0.75rem;
    padding: 0.65rem 0.9rem;
    background: rgba(30, 41, 59, 0.85);
    border: 1px solid rgba(148, 163, 184, 0.45);
    color: #f8fafc;
    font-size: 0.95rem;
    transition: border 0.25s ease, box-shadow 0.25s ease;

    &:focus {
        border-color: rgba(59, 130, 246, 0.75);
        outline: none;
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 0.85rem;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin-top: 1rem;

    ${tablet({
        justifyContent: "flex-start",
        marginTop: "1.15rem",
    })}

    ${mobile({
        flexDirection: "column",
        alignItems: "stretch",
        gap: "0.75rem",
        marginTop: "1.25rem",
    })}
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    border-radius: 9999px;
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
    color: #ffffff;

    &.primary {
        background: linear-gradient(135deg, #ef4444, #f97316);
        box-shadow: 0 10px 25px rgba(239, 68, 68, 0.35);
        min-width: 180px;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(249, 115, 22, 0.4);
        }
    }

    &.secondary {
        background: linear-gradient(135deg, #111827, #1f2937);
        border: 1px solid rgba(148, 163, 184, 0.45);

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(148, 163, 184, 0.25);
        }
    }
`;

const LuxuryTable = styled.div`
    background: linear-gradient(
        135deg,
        rgba(15, 23, 42, 0.92) 0%,
        rgba(30, 41, 59, 0.92) 100%
    );
    border-radius: 20px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.15);
    overflow: hidden;
`;

const GlassTableScroll = styled.div`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
`;

const GlassTable = styled.table`
    width: 100%;
    min-width: 480px;
    border-collapse: separate;
    border-spacing: 0;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
`;

const GlassTh = styled.th`
    text-align: left;
    padding: 0.9rem 1.25rem;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #a5f3fc;
    background: linear-gradient(
        180deg,
        rgba(15, 23, 42, 0.98) 0%,
        rgba(30, 41, 59, 0.92) 100%
    );
    border-bottom: 2px solid rgba(34, 211, 238, 0.45);
`;

const GlassTd = styled.td`
    padding: 0.95rem 1.25rem;
    font-size: 0.875rem;
    color: rgba(248, 250, 252, 0.95);
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
`;

const GlassTdLead = styled(GlassTd)`
    font-weight: 700;
    color: #fef3c7;
`;

const ProgressCell = styled(GlassTd)`
    min-width: 220px;
`;

const ProgressTrack = styled.div`
    width: 100%;
    height: 10px;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.25);
    overflow: hidden;
`;

const ProgressFill = styled.div`
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #22d3ee 0%, #6366f1 100%);
    box-shadow: 0 0 14px rgba(34, 211, 238, 0.3);
`;

const ProgressMeta = styled.div`
    margin-top: 0.35rem;
    font-size: 0.75rem;
    color: rgba(203, 213, 225, 0.95);
`;

const GlassTr = styled.tr`
    &:nth-child(even) td {
        background: rgba(15, 23, 42, 0.45);
    }
    &:nth-child(odd) td {
        background: rgba(15, 23, 42, 0.2);
    }
    &:hover td {
        background: rgba(34, 211, 238, 0.12) !important;
    }
`;

const GlassTbody = styled.tbody`
    tr:last-child td {
        border-bottom: none;
    }
`;

const SummaryTotalTr = styled(GlassTr)`
    && td {
        background: linear-gradient(
            95deg,
            rgba(76, 29, 149, 0.55) 0%,
            rgba(49, 46, 129, 0.72) 45%,
            rgba(30, 27, 75, 0.88) 100%
        ) !important;
        border-top: 2px solid rgba(167, 139, 250, 0.65);
        color: #f5f3ff;
    }
    && td:first-of-type {
        font-weight: 800;
        color: #e9d5ff;
        text-shadow: 0 0 12px rgba(167, 139, 250, 0.35);
    }
`;

const SummaryRemainingTr = styled(GlassTr)`
    && td {
        background: linear-gradient(
            90deg,
            rgba(6, 78, 59, 0.55) 0%,
            rgba(15, 118, 110, 0.38) 100%
        ) !important;
        border-top: 1px solid rgba(45, 212, 191, 0.35);
        font-size: 0.8125rem;
        color: rgba(240, 253, 250, 0.95);
    }
    && td:first-of-type {
        font-weight: 600;
        color: #99f6e4;
    }
`;

const SummaryPaceTr = styled(GlassTr)`
    && td {
        background: linear-gradient(
            90deg,
            rgba(113, 63, 18, 0.5) 0%,
            rgba(69, 26, 3, 0.55) 100%
        ) !important;
        border-top: 1px solid rgba(251, 191, 36, 0.28);
        font-size: 0.8125rem;
        color: rgba(255, 251, 235, 0.95);
    }
    && td:first-of-type {
        font-weight: 600;
        color: #fde68a;
    }
`;

const TableEmptyState = styled.div`
    padding: 2rem 1.5rem;
    text-align: center;
    font-size: 0.875rem;
    color: rgba(156, 163, 175, 0.95);
`;

const GlassChartSection = styled(ChartSection)`
    background: rgba(20, 30, 50, 0.95);
    border-radius: 16px;
    box-shadow: 0 4px 32px 0 #00eaff22;
    padding: 1.5rem 1.2rem;
    border: 1.5px solid #2e3a5e;
    margin-bottom: 1rem;
`;

const SubText = styled.p`
    color: rgba(209, 213, 219, 0.85);
    font-size: 0.8rem;
    margin-top: -0.35rem;
    margin-bottom: 1rem;
    line-height: 1.55;

    strong {
        color: rgba(255, 255, 255, 0.92);
        font-weight: 600;
    }
`;

const BigAmount = styled.div`
    font-size: 2.2rem;
    font-weight: 700;
    color: #ffffff;
    text-shadow: 0 0 12px #2062f744;
    margin-bottom: 1rem;
`;

const DesktopOnly = styled.div`
    ${mobile({
        display: "none",
    })}
`;

const MobileOnly = styled.div`
    display: none;

    ${mobile({
        display: "block",
    })}
`;

const ChartCanvas = styled.div`
    padding: 1.5rem 0.5rem;
`;

const ChartTotalCard = styled.div`
    text-align: center;
    margin-top: 1.5rem;
    padding: 1rem;
    background: rgba(59, 130, 246, 0.1);
    border-radius: 0.75rem;
    border: 1px solid rgba(59, 130, 246, 0.3);
`;

const ChartTotalLabel = styled.div`
    font-size: 0.85rem;
    color: #94a3b8;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
`;

const ChartTotalValue = styled.div`
    font-size: 1.75rem;
    font-weight: 700;
    color: #ffffff;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
`;

const MobileTrendList = styled.div`
    display: grid;
    gap: 0.75rem;
    max-height: 320px;
    overflow-y: auto;
    padding: 0.25rem 0.1rem 0.1rem;
    -webkit-overflow-scrolling: touch;
`;

const MobileTrendCard = styled.div`
    background: rgba(30, 41, 59, 0.9);
    border: 1px solid rgba(59, 130, 246, 0.4);
    border-radius: 12px;
    padding: 0.9rem 0.95rem;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.45);
`;

const MobileTrendRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MobileTrendLabel = styled.span`
    font-size: 0.78rem;
    color: #9ca3af;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-weight: 600;
`;

const MobileTrendValue = styled.span`
    font-size: 0.95rem;
    font-weight: 700;
    color: #e5e7eb;
    text-align: right;
`;

const CompactTotal = styled.div`
    margin-top: 1rem;
    padding: 1rem 0.95rem;
    background: rgba(59, 130, 246, 0.12);
    border: 1px solid rgba(59, 130, 246, 0.35);
    border-radius: 12px;
    box-shadow: 0 10px 24px rgba(59, 130, 246, 0.2);
`;

const CompactTotalLabel = styled.div`
    font-size: 0.82rem;
    color: #94a3b8;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 0.35rem;
    font-weight: 700;
`;

const CompactTotalValue = styled.div`
    font-size: 1.4rem;
    font-weight: 800;
    color: #ffffff;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 860px;

    ${mobile({
        minWidth: "100%",
    })}
`;

const TableHead = styled.thead`
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.45), rgba(14, 165, 233, 0.5));

    ${mobile({
        display: "none",
    })}
`;

const TableHeaderCell = styled.th`
    padding: 0.85rem 1rem;
    text-align: left;
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 700;
    color: #0f172a;
    border-bottom: 1px solid rgba(148, 163, 184, 0.35);
`;

const TableBody = styled.tbody`
    background: transparent;

`;

const TableRow = styled.tr`
    transition: background 0.25s ease;

    &:nth-child(even) {
        background: rgba(30, 41, 59, 0.65);
    }

    &:hover {
        background: rgba(59, 130, 246, 0.25);
    }

    ${mobile({
        display: "block",
        marginBottom: "1rem",
        borderRadius: "12px",
        border: "1px solid rgba(148, 163, 184, 0.25)",
        overflow: "hidden",
        background: "rgba(30, 41, 59, 0.75)",
    })}
`;

const TableCell = styled.td`
    padding: 0.9rem 1rem;
    border-bottom: 1px solid rgba(148, 163, 184, 0.25);
    font-size: 0.92rem;
    color: #e2e8f0;

    ${mobile({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.85rem 1rem",
        fontSize: "0.9rem",
        "&::before": {
            content: "attr(data-label)",
            fontWeight: 600,
            color: "#9ca3af",
            marginRight: "1rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontSize: "0.7rem",
        },
        "&:last-child": {
            borderBottom: "none",
        },
    })}
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 3.5rem 1rem;
    color: rgba(226, 232, 240, 0.75);
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    gap: 1rem;
`;

const Spinner = styled.div`
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 999px;
    border: 3px solid rgba(148, 163, 184, 0.35);
    border-top-color: rgba(59, 130, 246, 0.85);
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

const SALESMAN_ANNUAL_GOAL = 2000000;
const TABLE_EMPTY = "—";

function targetProgressPercent(currentAmount, targetAmount) {
    const current = Number(currentAmount) || 0;
    const target = Number(targetAmount) || 0;
    if (target <= 0) return 0;
    return Math.max(0, Math.min(100, (current / target) * 100));
}

export default function Report() {
    const dispatch = useDispatch();
    const salesReport = useSelector((state) => state.order.order);
    const currentUser = useSelector((state) => state.user.currentUser);
    const isFetching = useSelector((state) => state.order.isFetching);

    const TwoDecimalFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const IntegerFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin === true : null
    );

    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [depositAmount, setDepositAmount] = useState(0);
    const [profitAmount, setProfitAmount] = useState(0);
    const [expensesAmount, setExpensesAmount] = useState(0);
    const [purchaseOrderTotal, setPurchaseOrderTotal] = useState(0);
    const [installedTotal, setInstalledTotal] = useState(0);
    const [averageCompletionDays, setAverageCompletionDays] = useState(0);
    const [totalUnits, setTotalUnits] = useState(0);
    const [longestCompletion, setLongestCompletion] = useState(0);
    const [shortestCompletion, setShortestCompletion] = useState(0);
    const [amountReport, setAmountReport] = useState({
        year: moment().year(),
        yearlyCurrentAmount: 0,
        months: [],
    });
    const [reportLoading, setReportLoading] = useState(false);
    const [salesmanReport, setSalesmanReport] = useState({
        year: moment().year(),
        salesmanAnnualGoal: SALESMAN_ANNUAL_GOAL,
        annualGoal: 0,
        salesmen: [],
        total: { yearToDate: 0, monthToDate: 0 },
        remaining: 0,
        weeksLeft: 0,
        perWeekRequired: 0,
        progressPercent: 0,
    });
    const [inputs, setInputs] = useState({
        principle: principle
            ? principle
            : superadmin 
            ? undefined
            : currentUser._id,
        createdBy: principle ? currentUser._id : superadmin ? undefined : undefined,
        dateFrom: moment(Date.now()).startOf("year").format("YYYY-MM-DD"),
        dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
    });

    const getAdmin = async () => {
        const res =
            process.env.REACT_APP_COUNTRY === "SINGAPORE"
                ? await publicRequest.get("/users/admin", {
                      params: { isAdmin: true },
                  })
                : await publicRequest.get("/users/superadmin", {
                      params: { isAdmin: true },
                  });
        setUsersData(res.data);
    };

    const handleDateFrom = (e) => {
        setInputs((prev) => {
            return { ...prev, dateFrom: e.target.value };
        });
    };
    const handleDateTo = (e) => {
        setInputs((prev) => {
            return { ...prev, dateTo: e.target.value };
        });
    };

    const handleResetFilters = () => {
        setInputs({
            principle: principle
                ? principle
                : superadmin 
                ? undefined
                : currentUser._id,
            createdBy: principle ? currentUser._id : superadmin ? undefined : undefined,
            dateFrom: moment(Date.now()).startOf("month").format("YYYY-MM-DD"),
            dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
        });
    };

    const [usersData, setUsersData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            setReportLoading(true);
            const [res, amountRes, salesmanRes] = await Promise.all([
                getReport(dispatch, inputs),
                getSalesAmountReport(inputs),
                getSalesmanReport(inputs),
            ]);
            setTotalAmount(0);
            setDepositAmount(0);
            setProfitAmount(0);
            setExpensesAmount(0);
            setPurchaseOrderTotal(0);
            setInstalledTotal(0);
            setAverageCompletionDays(0);
            setTotalUnits(0);
            setLongestCompletion(0);
            setShortestCompletion(0);
            setModalData(res || []);
            setAmountReport(
                amountRes || {
                    year: moment(inputs.dateFrom).year(),
                    yearlyCurrentAmount: 0,
                    months: [],
                }
            );
            setSalesmanReport(
                salesmanRes || {
                    year: moment(inputs.dateFrom).year(),
                    salesmanAnnualGoal: SALESMAN_ANNUAL_GOAL,
                    annualGoal: 0,
                    salesmen: [],
                    total: { yearToDate: 0, monthToDate: 0 },
                    remaining: 0,
                    weeksLeft: 0,
                    perWeekRequired: 0,
                    progressPercent: 0,
                }
            );
            setIsLoaded(true);
            setReportLoading(false);

            if (res && Array.isArray(res) && res.length > 0) {
                let totalAmountAccumulator = 0;
                let depositAccumulator = 0;
                let profitAccumulator = 0;
                let expensesAccumulator = 0;
                let purchaseOrderAccumulator = 0;
                let installedAccumulator = 0;
                let totalDaysAccumulator = 0;
                let totalUnitsAccumulator = 0;
                let longestTimeAccumulator = 0;
                let shortestTimeAccumulator = Number.POSITIVE_INFINITY;

                for (const item of res) {
                        totalAmountAccumulator += item.monthlySales || 0;
                        depositAccumulator += item.monthlyDeposit || 0;
                        profitAccumulator += item.profit || 0;
                        expensesAccumulator += item.expenses || 0;
                        purchaseOrderAccumulator += item.purchaseOrders || 0;
                        installedAccumulator += item.totalInstalled || 0;
                        totalDaysAccumulator += item.totalDaysTakenToComplete || 0;
                        totalUnitsAccumulator += item.salesAmount || 0;
                        longestTimeAccumulator = Math.max(longestTimeAccumulator, item.longestTimeTakenToComplete || 0);
                        if (item.shortestTimeTakenToComplete || item.shortestTimeTakenToComplete === 0) {
                            shortestTimeAccumulator = Math.min(shortestTimeAccumulator, item.shortestTimeTakenToComplete);
                        }
                }

                setTotalAmount(totalAmountAccumulator);
                setDepositAmount(depositAccumulator);
                setProfitAmount(profitAccumulator);
                setExpensesAmount(expensesAccumulator);
                setPurchaseOrderTotal(purchaseOrderAccumulator);
                setInstalledTotal(installedAccumulator);
                setAverageCompletionDays(
                    totalDaysAccumulator
                );
                setTotalUnits(totalUnitsAccumulator);
                setLongestCompletion(longestTimeAccumulator);
                setShortestCompletion(shortestTimeAccumulator === Number.POSITIVE_INFINITY ? 0 : shortestTimeAccumulator);
            }
        };
        fetchData();
    }, [dispatch, inputs]);

    useEffect(() => {
        if (usersData[0] === undefined) {
            getAdmin();
        }
    }, [usersData]);

    const videoRef = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                video.pause();
                return;
            }
            video.play().catch(() => {});
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    const getCurrencySymbol = (report) =>
        process.env.REACT_APP_COUNTRY === "SINGAPORE" ||
        report?.outlet === "HAOFA Tourbillon Singapore"
            ? "$"
            : "RM";

    const columns = [
        {
            Header: "Dealer",
            accessor: "outlet",
            Cell: ({ value }) => (
                <div className="text-sm font-medium">{value ? value : "Unknown Outlet"}</div>
            ),
        },
        {
            Header: "Total Cases",
            accessor: "salesAmount",
            Cell: ({ value }) => (
                <div className="text-sm">{value !== undefined && value !== null ? value : 0}</div>
            ),
        },
        {
            Header: "Total Sales",
            accessor: "totalSales",
            Cell: ({ row }) => (
                <div className="text-sm font-bold">
                    {getCurrencySymbol(row.original)}
                    {TwoDecimalFormatter.format(row.original.totalSales || 0)}
                </div>
            ),
        },
        {
            Header: "Profit",
            accessor: "profit",
            Cell: ({ value, row }) => (
                <div className="text-sm">
                    {getCurrencySymbol(row.original)}
                    {TwoDecimalFormatter.format(value || 0)}
                </div>
            ),
        },
        {
            Header: "Expenses",
            accessor: "expenses",
            Cell: ({ value, row }) => (
                <div className="text-sm">
                    {getCurrencySymbol(row.original)}
                    {TwoDecimalFormatter.format(value || 0)}
                </div>
            ),
        },
        {
            Header: "Purchase Orders",
            accessor: "purchaseOrders",
            Cell: ({ value }) => (
                <div className="text-sm">{value !== undefined && value !== null ? value : 0}</div>
            ),
        },
        {
            Header: "Total Installed",
            accessor: "totalInstalled",
            Cell: ({ value }) => (
                <div className="text-sm">{value !== undefined && value !== null ? value : 0}</div>
            ),
        },
        {
            Header: "Average Completion Days",
            accessor: "totalDaysTakenToComplete",
            Cell: ({ value }) => (
                <div className="text-sm">{value !== undefined && value !== null ? TwoDecimalFormatter.format(value) : "0.00"}</div>
            ),
        },
        {
            Header: "Longest Completion Time",
            accessor: "longestTimeTakenToComplete",
            Cell: ({ value }) => (
                <div className="text-sm">{value !== undefined && value !== null ? value : 0}</div>
            ),
        },
        {
            Header: "Shortest Completion Time",
            accessor: "shortestTimeTakenToComplete",
            Cell: ({ value }) => (
                <div className="text-sm">{value !== undefined && value !== null ? value : 0}</div>
            ),
        },
    ];

    const currencySymbol = process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM";

    const formatCurrency = (value) => `${currencySymbol}${TwoDecimalFormatter.format(value || 0)}`;

    const salesmanGoal =
        salesmanReport.salesmanAnnualGoal || SALESMAN_ANNUAL_GOAL;
    const combinedAnnualGoal =
        salesmanReport.annualGoal ||
        (salesmanReport.salesmen?.length || 0) * salesmanGoal;

    const renderProgressCell = (currentAmount, targetAmount) => {
        const progress = targetProgressPercent(currentAmount, targetAmount);
        return (
            <>
                <ProgressTrack>
                    <ProgressFill style={{ width: `${progress}%` }} />
                </ProgressTrack>
                <ProgressMeta>
                    {formatCurrency(currentAmount)} / {formatCurrency(targetAmount)} (
                    {progress.toFixed(1)}%)
                </ProgressMeta>
            </>
        );
    };

    const summaryStats = [
        {
            title: "Total Amount",
            value: formatCurrency(totalAmount),
            description: "Sales across outlets",
            icon: <MdPayment />,
        },
        {
            title: "Total Cases",
            value: IntegerFormatter.format(totalUnits || 0),
            description: "Units sold",
            icon: <MdLocalAtm />,
        },
        {
            title: "Total Profit",
            value: formatCurrency(profitAmount),
            description: "Aggregate profit",
            icon: <MdLoyalty />,
        },
        {
            title: "Total Expenses",
            value: formatCurrency(expensesAmount),
            description: "Reported expenses for the period",
            icon: <MdLightbulbOutline />,
        },
        {
            title: "Total Purchase Orders",
            value: formatCurrency(purchaseOrderTotal || 0),
            description: "Number of purchase orders",
            icon: <MdAssignmentTurnedIn />,
        },
        {
            title: "Total Installed",
            value: IntegerFormatter.format(installedTotal || 0),
            description: "Cases successfully installed",
            icon: <MdCardTravel />,
        },
        {
            title: "Average Completion Days",
            value: `${TwoDecimalFormatter.format(averageCompletionDays || 0)} days`,
            description: "Average completion time per outlet",
            icon: <MdQueryStats />,
        },
        {
            title: "Longest Completion Time",
            value: `${IntegerFormatter.format(longestCompletion || 0)} days`,
            description: "Longest completion time recorded",
            icon: <MdAccessTime />,
        },
        {
            title: "Shortest Completion",
            value: `${IntegerFormatter.format(shortestCompletion || 0)} days`,
            description: "Fastest completion time recorded",
            icon: <MdTimerOff />,
        },
    ];

    const hasData =
        isLoaded && !isFetching && modalData && Array.isArray(modalData) && modalData.length > 0;

    const salesChartData = useMemo(
        () =>
            (amountReport.months || []).map((row) => ({
                name: row.monthName,
                sales: row.currentAmount || 0,
                invoices: row.invoiceCount || 0,
            })),
        [amountReport.months]
    );

    const totalChartSales = amountReport.yearlyCurrentAmount || 0;
    const hasSalesChartData =
        !reportLoading && salesChartData.length > 0;

    return (
        <DashboardBg>
            <VideoBackground
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
            >
                <source
                    src="https://ik.imagekit.io/dnddecpho/Inhome/animatedbg_Rx2Blqd5L.mp4?updatedAt=1746618975517"
                    type="video/mp4"
                />
            </VideoBackground>
            <BackgroundOverlay />
            <HudOverlay />
            <DashboardContainer>
                <PageHeader>
                    <PageTitle>Performance Report</PageTitle>
                    <PageSubtitle>
                        Comprehensive overview of outlets, profitability, and completion efficiency
                    </PageSubtitle>
                </PageHeader>

                <ChartSection>
                    <BlueChartTitle>Filters</BlueChartTitle>
                    <FiltersGrid>
                        <FilterCard>
                            <FilterLabel htmlFor="dateFrom">Date From</FilterLabel>
                            <FilterInput
                                id="dateFrom"
                                name="dateFrom"
                                type="date"
                                value={inputs.dateFrom}
                                onChange={handleDateFrom}
                            />
                        </FilterCard>
                        <FilterCard>
                            <FilterLabel htmlFor="dateTo">Date To</FilterLabel>
                            <FilterInput
                                id="dateTo"
                                name="dateTo"
                                type="date"
                                value={inputs.dateTo}
                                onChange={handleDateTo}
                            />
                        </FilterCard>
                    </FiltersGrid>
                    <ActionButtons>
                        <ActionButton type="button" className="primary" onClick={handleResetFilters}>
                            <FiPlus />
                            Reset Filters
                        </ActionButton>
                    </ActionButtons>
                </ChartSection>

                <ChartSection>
                    <ChartTitle>Summary Overview</ChartTitle>
                    <StatsGrid>
                        {summaryStats.map((stat) => (
                            <StatCard key={stat.title}>
                                <StatIcon>{stat.icon}</StatIcon>
                                <StatTitle>{stat.title}</StatTitle>
                                <StatValue>{stat.value}</StatValue>
                                {stat.description && <StatDescription>{stat.description}</StatDescription>}
                            </StatCard>
                        ))}
                    </StatsGrid>
                </ChartSection>

                {hasSalesChartData && (
                    <ChartSection>
                        <ChartTitle>Sales Trend</ChartTitle>
                        <DesktopOnly>
                            <ChartCanvas>
                                <ResponsiveContainer width="100%" height={350}>
                                    <LineChart
                                        data={salesChartData}
                                        margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="rgba(148, 163, 184, 0.2)"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            stroke="#94a3b8"
                                            style={{
                                                fontSize: "0.85rem",
                                                fontFamily: "Inter, Helvetica, Arial, sans-serif",
                                            }}
                                        />
                                        <YAxis
                                            stroke="#94a3b8"
                                            style={{
                                                fontSize: "0.85rem",
                                                fontFamily: "Inter, Helvetica, Arial, sans-serif",
                                            }}
                                            tickFormatter={(value) =>
                                                `${currencySymbol}${(value / 1000).toFixed(0)}k`
                                            }
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: "rgba(17, 24, 39, 0.95)",
                                                border: "1px solid rgba(59, 130, 246, 0.3)",
                                                borderRadius: "0.75rem",
                                                color: "#e2e8f0",
                                                fontFamily: "Inter, Helvetica, Arial, sans-serif",
                                                fontSize: "0.9rem",
                                                padding: "0.75rem 1rem",
                                            }}
                                            formatter={(value) => [
                                                formatCurrency(value),
                                                "Sales",
                                            ]}
                                            labelFormatter={(label) => `Month: ${label}`}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                paddingTop: "1rem",
                                                fontFamily: "Inter, Helvetica, Arial, sans-serif",
                                                fontSize: "0.9rem",
                                                color: "#e2e8f0",
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="sales"
                                            stroke="#3b82f6"
                                            strokeWidth={3}
                                            dot={{ fill: "#3b82f6", r: 5 }}
                                            activeDot={{ r: 7, fill: "#60a5fa" }}
                                            name="Current Amount"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                                <ChartTotalCard>
                                    <ChartTotalLabel>Yearly Current Amount</ChartTotalLabel>
                                    <ChartTotalValue>
                                        {formatCurrency(totalChartSales)}
                                    </ChartTotalValue>
                                </ChartTotalCard>
                            </ChartCanvas>
                        </DesktopOnly>

                        <MobileOnly>
                            <MobileTrendList>
                                {salesChartData.map((item, index) => (
                                    <MobileTrendCard key={`${item.name || "month"}-${index}`}>
                                        <MobileTrendRow>
                                            <MobileTrendLabel>Month</MobileTrendLabel>
                                            <MobileTrendValue>{item.name || "N/A"}</MobileTrendValue>
                                        </MobileTrendRow>
                                        <MobileTrendRow>
                                            <MobileTrendLabel>Current Amount</MobileTrendLabel>
                                            <MobileTrendValue>
                                                {formatCurrency(item.sales || 0)}
                                            </MobileTrendValue>
                                        </MobileTrendRow>
                                        <MobileTrendRow>
                                            <MobileTrendLabel>Invoice Count</MobileTrendLabel>
                                            <MobileTrendValue>
                                                {IntegerFormatter.format(item.invoices || 0)}
                                            </MobileTrendValue>
                                        </MobileTrendRow>
                                    </MobileTrendCard>
                                ))}
                            </MobileTrendList>
                            <CompactTotal>
                                <CompactTotalLabel>Yearly Current Amount</CompactTotalLabel>
                                <CompactTotalValue>
                                    {formatCurrency(totalChartSales)}
                                </CompactTotalValue>
                            </CompactTotal>
                        </MobileOnly>
                    </ChartSection>
                )}

                <GlassChartSection>
                    <BlueChartTitle>Sales by Salesman</BlueChartTitle>
                    <SubText>
                        Sales from{" "}
                        <strong>
                            {salesmanReport.yearStart || `1 Jan ${salesmanReport.year}`}
                        </strong>{" "}
                        through{" "}
                        <strong>
                            {salesmanReport.yearEnd || `31 Dec ${salesmanReport.year}`}
                        </strong>{" "}
                        (by order date). Each salesman target is{" "}
                        <strong>{formatCurrency(salesmanGoal)}</strong>.{" "}
                        <strong>Total</strong> row progress is combined YTD vs{" "}
                        <strong>{formatCurrency(combinedAnnualGoal)}</strong>.
                    </SubText>
                    <LuxuryTable>
                        {reportLoading ? (
                            <TableEmptyState>Loading salesman sales data…</TableEmptyState>
                        ) : !(salesmanReport.salesmen || []).length ? (
                            <TableEmptyState>No salesman sales data available.</TableEmptyState>
                        ) : (
                            <GlassTableScroll>
                                <GlassTable>
                                    <thead>
                                        <tr>
                                            <GlassTh>Salesman</GlassTh>
                                            <GlassTh>Year to date</GlassTh>
                                            <GlassTh>Month to date</GlassTh>
                                            <GlassTh>Progress</GlassTh>
                                        </tr>
                                    </thead>
                                    <GlassTbody>
                                        {(salesmanReport.salesmen || []).map((row) => (
                                            <GlassTr key={row.id || row.name}>
                                                <GlassTdLead>{row.name}</GlassTdLead>
                                                <GlassTd>
                                                    {formatCurrency(row.yearToDate || 0)}
                                                </GlassTd>
                                                <GlassTd>
                                                    {formatCurrency(row.monthToDate || 0)}
                                                </GlassTd>
                                                <ProgressCell>
                                                    {renderProgressCell(
                                                        row.yearToDate || 0,
                                                        row.annualGoal || salesmanGoal
                                                    )}
                                                </ProgressCell>
                                            </GlassTr>
                                        ))}
                                        <SummaryTotalTr>
                                            <GlassTdLead>Total</GlassTdLead>
                                            <GlassTd>
                                                {formatCurrency(
                                                    salesmanReport.total?.yearToDate || 0
                                                )}
                                            </GlassTd>
                                            <GlassTd>
                                                {formatCurrency(
                                                    salesmanReport.total?.monthToDate || 0
                                                )}
                                            </GlassTd>
                                            <ProgressCell>
                                                {renderProgressCell(
                                                    salesmanReport.total?.yearToDate || 0,
                                                    combinedAnnualGoal
                                                )}
                                            </ProgressCell>
                                        </SummaryTotalTr>
                                        <SummaryRemainingTr>
                                            <GlassTd>Remaining</GlassTd>
                                            <GlassTd>
                                                {formatCurrency(salesmanReport.remaining || 0)}
                                            </GlassTd>
                                            <GlassTd>{TABLE_EMPTY}</GlassTd>
                                            <GlassTd>{TABLE_EMPTY}</GlassTd>
                                        </SummaryRemainingTr>
                                        <SummaryPaceTr>
                                            <GlassTd>
                                                {salesmanReport.weeksLeft || 0} week
                                                {salesmanReport.weeksLeft === 1 ? "" : "s"} left
                                            </GlassTd>
                                            <GlassTd>
                                                {formatCurrency(
                                                    salesmanReport.perWeekRequired || 0
                                                )}{" "}
                                                per week
                                            </GlassTd>
                                            <GlassTd>{TABLE_EMPTY}</GlassTd>
                                            <GlassTd>{TABLE_EMPTY}</GlassTd>
                                        </SummaryPaceTr>
                                    </GlassTbody>
                                </GlassTable>
                            </GlassTableScroll>
                        )}
                    </LuxuryTable>
                </GlassChartSection>

                <GlassChartSection>
                    <BlueChartTitle>Yearly Current Amount</BlueChartTitle>
                    {reportLoading ? (
                        <SubText>Loading…</SubText>
                    ) : (
                        <BigAmount>
                            {formatCurrency(amountReport.yearlyCurrentAmount || 0)}
                        </BigAmount>
                    )}
                </GlassChartSection>

                <GlassChartSection>
                    <BlueChartTitle>Current Amount</BlueChartTitle>
                    <LuxuryTable>
                        {reportLoading ? (
                            <TableEmptyState>Loading sales data…</TableEmptyState>
                        ) : !(amountReport.months || []).length ? (
                            <TableEmptyState>No sales data available.</TableEmptyState>
                        ) : (
                            <GlassTableScroll>
                                <GlassTable>
                                    <thead>
                                        <tr>
                                            <GlassTh>Month</GlassTh>
                                            <GlassTh>Current Amount</GlassTh>
                                            <GlassTh>Invoice Count</GlassTh>
                                        </tr>
                                    </thead>
                                    <GlassTbody>
                                        {(amountReport.months || []).map((row) => (
                                            <GlassTr key={row.monthName || row.month}>
                                                <GlassTd>{row.monthName}</GlassTd>
                                                <GlassTdLead>
                                                    {formatCurrency(row.currentAmount || 0)}
                                                </GlassTdLead>
                                                <GlassTd>
                                                    {IntegerFormatter.format(
                                                        row.invoiceCount || 0
                                                    )}
                                                </GlassTd>
                                            </GlassTr>
                                        ))}
                                    </GlassTbody>
                                </GlassTable>
                            </GlassTableScroll>
                        )}
                    </LuxuryTable>
                </GlassChartSection>

                {superadmin && (
                    <ChartSection>
                        <ChartTitle>Dealer Breakdown</ChartTitle>
                        <LuxuryTable>
                            {hasData ? (
                                <StyledTable>
                                    <TableHead>
                                        <tr>
                                            {columns.map((column) => (
                                                <TableHeaderCell
                                                    key={column.accessor || column.Header}
                                                >
                                                    {column.Header}
                                                </TableHeaderCell>
                                            ))}
                                        </tr>
                                    </TableHead>
                                    <TableBody>
                                        {modalData.map((report, index) => (
                                            <TableRow key={`${report.id || report.outlet || "row"}-${index}`}>
                                                {columns.map((column) => (
                                                    <TableCell
                                                        key={`${column.accessor || column.Header}-${index}`}
                                                        data-label={column.Header}
                                                    >
                                                        {column.Cell
                                                            ? column.Cell({
                                                                  value: report[column.accessor],
                                                                  row: { original: report },
                                                              })
                                                            : report[column.accessor] !== undefined &&
                                                              report[column.accessor] !== null
                                                            ? report[column.accessor]
                                                            : "-"}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </StyledTable>
                            ) : isLoaded && !isFetching ? (
                                <EmptyState>
                                    <div style={{ fontSize: "1.15rem", fontWeight: 600, marginBottom: "0.6rem" }}>
                                        No data found
                                    </div>
                                    <div style={{ fontSize: "0.95rem", maxWidth: "520px", margin: "0 auto" }}>
                                        No sales data found for the selected date range. Try adjusting your filters or
                                        ensure there are recent orders from your outlets.
                                    </div>
                                </EmptyState>
                            ) : (
                                <LoaderWrapper>
                                    <Spinner />
                                    <span>Loading report data...</span>
                                </LoaderWrapper>
                            )}
                        </LuxuryTable>
                    </ChartSection>
                )}
            </DashboardContainer>
        </DashboardBg>
    );
}
